import CountUp from "./CountUp"
import Reveal from "./Reveal"
import { breakdownRows, computeTotal, percentMarkup } from "../lib/comparisonWidgetTotal"
import COMPARISON_DISCLAIMER from "../../../config/comparison_disclaimer.json"

// Renders any comparison widget from a recipe (schema) + campaign data.
// The recipe contract is config/schemas/comparison_widget_schema.json — the
// same JSON Schema the model validates against and the MCP tools hand to
// Claude when authoring. New two-card comparison variants are recipe-only;
// genuinely new LAYOUTS need code here.

function formatValue(value, unit) {
  const num = Number(value) || 0
  const decimals = unit.decimals ?? 2
  return `${unit.prefix ?? "$"}${num.toFixed(decimals)}${unit.suffix ?? ""}`
}

// Resolve {{field_key}} tokens from campaign data, plus computed tokens
// (currently {{percent_markup}}). React escapes output — display strings
// are plain text, never HTML.
function resolveTokens(template, data, computed) {
  if (!template) return ""
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    if (key in computed) return String(computed[key])
    const v = data[key]
    return v == null ? "" : String(v)
  })
}

function BreakdownRows({ schema, data, leftValue, unit, tokens }) {
  const { display } = schema
  // Row SELECTION moved to lib/comparisonWidgetTotal.js (act:829d77da) so the
  // Ruby noscript twin has a shared contract to be locked against. This
  // component still owns PRESENTATION; it no longer owns which rows exist.
  const rows = breakdownRows(schema, data)
  // A breakdown with no additions would only restate the left value and the
  // total — which the two big cards already show. Skip it (e.g. the protein
  // advertised-vs-tested widget).
  if (rows.length === 0) return null

  const baseLabel = resolveTokens(display.breakdown_base_label, data, tokens) || "Subtotal"
  const total = tokens.__total

  return (
    <div className="mt-5 text-left text-sm">
      <div className="flex justify-between py-1.5 border-b border-gray-100">
        <span className="text-body">{baseLabel}</span>
        <span className="text-ink">{formatValue(leftValue, unit)}</span>
      </div>

      {rows.map((row, idx) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: read-only display rows re-derived each render; no reorder, no state, nothing for a mis-keyed node to carry.
        <div key={`row-${idx}`} className="flex justify-between py-1.5 border-b border-gray-100">
          <span className="text-body">{row.label ?? `Item ${idx + 1}`}</span>
          <span className={row.kind === "item" ? "text-brand font-medium" : "text-ink"}>
            +{formatValue(row.amount, unit)}
          </span>
        </div>
      ))}

      {display.total_row_label && (
        <div className="flex justify-between py-1.5 mt-1 font-semibold">
          <span className="text-body">{resolveTokens(display.total_row_label, data, tokens)}</span>
          <span className="text-brand">
            {formatValue(total, unit)}
            {display.total_row_suffix || ""}
          </span>
        </div>
      )}
    </div>
  )
}

export default function SchemaWidget({ schema, data, disclaimer }) {
  if (!schema?.fields || !schema.display) return null
  if (!data || typeof data !== "object" || Object.keys(data).length === 0) return null

  // The standing legal disclaimer (act:38d3b122) — rendered on EVERY chart this
  // component draws, after the null guards above so a chartless theory renders
  // neither chart nor disclaimer. The prop is the resolved firm-settings value
  // (adjustable by the firm, no deploy); the JSON import is the SAME file that
  // seeds that setting's default, so a render site that forgets the prop still
  // shows the standing wording rather than nothing — fallback, not the path.
  const disclaimerText =
    typeof disclaimer === "string" && disclaimer.trim()
      ? disclaimer
      : COMPARISON_DISCLAIMER.disclaimer

  const { fields, display } = schema
  const unit = display.unit || {}
  const leftValue = Number(data[display.left_value_field]) || 0
  const total = computeTotal(schema, data)
  const markup = percentMarkup(schema, data)
  const tokens = { percent_markup: markup, __total: total }

  const checklistField = display.left_checklist_field
    ? fields.find((f) => f.key === display.left_checklist_field && f.type === "checklist_items")
    : null
  const checklistItems =
    checklistField && Array.isArray(data[checklistField.key]) ? data[checklistField.key] : []
  const checklistLabelKey = checklistField
    ? (checklistField.item_fields || []).find((f) => f.type === "text")?.key
    : null
  // Headline sizes match the original variants: smaller when a checklist follows
  const valueSize = checklistField ? "text-3xl" : "text-5xl"

  const leftTitle = resolveTokens(display.left_title, data, tokens)
  const rightTitle = resolveTokens(display.right_title, data, tokens)
  const leftSubtitle = resolveTokens(display.left_subtitle, data, tokens)
  const rightSubtitle = resolveTokens(display.right_subtitle, data, tokens)

  return (
    <Reveal as="section" className="max-w-3xl mx-auto px-4 py-10">
      {/* Programmatic section heading (accessibility-0002): the comparison is a
          distinct section but its two card titles are styled labels, not
          headings, so a screen-reader / heading-navigation user had nothing to
          land on. sr-only keeps the visual design unchanged while giving the
          section a real <h2> — parity with the <noscript> static twin, which
          already emits this heading. */}
      <h2 className="sr-only">
        {leftTitle} vs. {rightTitle}
      </h2>
      <div className="flex flex-wrap justify-center gap-6">
        {/* Left card — what they promised/showed.
            small-screen-0005: below sm the card is w-full (one per row, it
            STACKS) so the large value text always has room — dropping flex-1's
            shrink-to-fit below sm, which otherwise squeezed two cards
            side-by-side until the 5xl price overflowed the viewport. The
            280px floor + two-up layout return at sm+. */}
        {/* Sober slate treatment for the "showed you" card (Establishment
            direction, act:bf2ebd5f): the harm reads as a calm neutral-vs-navy
            comparison, not green-vs-red — the emphasis color lives on the
            "actually paid" card only. Slate-600/700 on white are 7.6:1/10.4:1. */}
        <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[280px] max-w-[330px] bg-white rounded-xl p-7 text-center border-2 border-slate-300 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5">
            {leftTitle}
          </div>
          {leftSubtitle && <div className="text-sm text-body mb-3">{leftSubtitle}</div>}
          <div
            className={`font-serif ${valueSize} font-bold text-slate-700 ${checklistField ? "mb-4" : ""}`}
          >
            {formatValue(leftValue, unit)}
            {display.left_value_suffix || ""}
          </div>
          {checklistItems.length > 0 && (
            <div className="text-left text-sm space-y-2">
              {checklistItems.map((item, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: read-only display rows re-derived each render; no reorder, no state, nothing for a mis-keyed node to carry.
                <div key={idx} className="flex items-center gap-2 py-1">
                  <span className="text-slate-600 text-lg">&#10003;</span>
                  <span className="text-ink">
                    {checklistLabelKey ? item[checklistLabelKey] : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right card — what you actually paid (same stack-below-sm rule as the
            left card, small-screen-0005). */}
        <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[280px] max-w-[330px] bg-white rounded-xl p-7 text-center border-2 border-brand shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl animate-wiggle">
          <div className="text-xs font-semibold uppercase tracking-wide text-brand mb-1.5">
            {rightTitle}
          </div>
          {rightSubtitle && <div className="text-sm text-body mb-3">{rightSubtitle}</div>}
          <div className={`font-serif ${valueSize} font-bold text-brand`}>
            <CountUp
              to={total}
              prefix={unit.prefix ?? "$"}
              suffix={`${unit.suffix ?? ""}${display.right_value_suffix || ""}`}
              decimals={unit.decimals ?? 2}
            />
          </div>
          <BreakdownRows
            schema={schema}
            data={data}
            leftValue={leftValue}
            unit={unit}
            tokens={tokens}
          />
        </div>
      </div>
      <p className="mt-4 text-center text-xs text-body">{disclaimerText}</p>
    </Reveal>
  )
}
